/* ═══════════════════════════════════════════════════════════════════════════
   CUSTOMERS REPORT — DRILL & PROFILE VIEWS
   · CUST-5.1  Shared Customer Details table (masked contacts + eye toggles)
   · CUST-9    Export with contact-consent dialog
   · CUST-7    Individual customer profile (header, timeline, order log)
   · CUST-7.4  Loyalty membership block  · CUST-16.1 Dispute history
   · CUST-17.1 RFM detail panel          · CUST-19.2 Acquisition source
   ═══════════════════════════════════════════════════════════════════════════ */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, useIsDesktop } from "./reportTokens";
import { Icon, InfoTip, SectionCard, SegmentBadge, Avatar, useMoney, PageSizeSelect, PaginationBar, ALL_ROWS } from "./customersReportShared";
import { exportTable } from "../../utils/reportExport";
import {
    CustomerRecord, CustomerOrder, maskEmail, maskPhone, segmentColor, channelIcon,
    orderLogTotals, orderDiscountTotal, openLostDisputes, isRepeatDisputer, acquisitionByKey,
    infoCopy,
} from "./customersReportData";

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-9 — EXPORT WITH CONTACT CONSENT
   Two-stage: format (Excel / PDF) → "Include contact information?" consent.
   ═══════════════════════════════════════════════════════════════════════════ */
export interface ExportFlowSpec {
    title: string;
    filename: string;
    /**
     * Build the export table; contacts full when includeContacts, else masked.
     * May be async: the Customer Directory builds its file from a fresh call to
     * the directory endpoint (the whole filtered set, not the page on screen),
     * while every other export resolves synchronously from data already loaded.
     */
    build: (includeContacts: boolean) =>
        | { headers: string[]; rows: (string | number)[][] }
        | Promise<{ headers: string[]; rows: (string | number)[][] }>;
}

export function ExportConsentDialog({ spec, onClose }: { spec: ExportFlowSpec; onClose: () => void }) {
    const [format, setFormat] = useState<"excel" | "pdf" | null>(null);

    const run = async (includeContacts: boolean) => {
        const fmt = format!;
        const { headers, rows } = await spec.build(includeContacts);
        await exportTable({ title: spec.title, headers, rows, filename: spec.filename, format: fmt });
        onClose();
    };

    return (
        <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,18,30,0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
            <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: 16, width: 400, maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.28)", overflow: "hidden" }}>
                {!format ? (
                    <>
                        <div style={{ padding: "18px 20px 6px", fontSize: 15, fontWeight: 700, color: T.t1 }}>Export {spec.title}</div>
                        <div style={{ padding: "0 20px 16px", fontSize: 12, color: T.t3 }}>Choose a file format.</div>
                        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                            <button onClick={() => setFormat("excel")} style={fmtBtn}>
                                <Icon name="table_view" size={18} color={T.green} /> Export as Excel (.xlsx)
                            </button>
                            <button onClick={() => setFormat("pdf")} style={fmtBtn}>
                                <Icon name="picture_as_pdf" size={18} color={T.accent} /> Export as PDF (.pdf)
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ padding: "18px 20px 6px", fontSize: 15, fontWeight: 700, color: T.t1 }}>Include contact information?</div>
                        <div style={{ padding: "0 20px 16px", fontSize: 12.5, color: T.t2, lineHeight: 1.5 }}>
                            This export contains customer data. Would you like to include full email addresses and phone numbers in the file?
                        </div>
                        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                            <button onClick={() => run(true)} style={{ ...consentBtn, background: T.accent, color: "#fff", border: "none" }}>Include contacts</button>
                            <button onClick={() => run(false)} style={{ ...consentBtn, background: T.surface, color: T.t1, border: `1px solid ${T.cardBorder}` }}>Exclude contacts</button>
                            <button onClick={onClose} style={{ ...consentBtn, background: "transparent", color: T.t3, border: "none" }}>Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
/**
 * CUST-9's "Include contact information?" step on its own, for the shared
 * `ReportSectionExportButton` (QA #11 / #12): the button owns format and outlet
 * scope like every other report, and calls `ask` as its `prepare` step. Resolves
 * "include" / "exclude", or `false` on Cancel, which the button reads as "abort".
 * Portalled above the export scope modal (z-index 1000).
 */
export type ContactConsent = "include" | "exclude";

export function useContactConsent() {
    const [pending, setPending] = useState<((v: ContactConsent | false) => void) | null>(null);

    const ask = () => new Promise<ContactConsent | false>((resolve) => {
        setPending(() => resolve);
    });
    const answer = (v: ContactConsent | false) => {
        pending?.(v);
        setPending(null);
    };

    const dialog = pending ? createPortal(
        <div
            onClick={() => answer(false)}
            style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,18,30,0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
            <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: 16, width: 400, maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.28)", overflow: "hidden", fontFamily: T.font }}>
                <div style={{ padding: "18px 20px 6px", fontSize: 15, fontWeight: 700, color: T.t1 }}>Include contact information?</div>
                <div style={{ padding: "0 20px 16px", fontSize: 12.5, color: T.t2, lineHeight: 1.5 }}>
                    This export contains customer data. Would you like to include full email addresses and phone numbers in the file?
                </div>
                <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => answer("include")} style={{ ...consentBtn, background: T.accent, color: "#fff", border: "none" }}>Include contacts</button>
                    <button onClick={() => answer("exclude")} style={{ ...consentBtn, background: T.surface, color: T.t1, border: `1px solid ${T.cardBorder}` }}>Exclude contacts</button>
                    <button onClick={() => answer(false)} style={{ ...consentBtn, background: "transparent", color: T.t3, border: "none" }}>Cancel</button>
                </div>
            </div>
        </div>,
        document.body,
    ) : null;

    return { ask, dialog };
}

const fmtBtn: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", fontSize: 13, fontWeight: 500, color: T.t1, textAlign: "left" };
const consentBtn: React.CSSProperties = { padding: "11px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600 };

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-5.1 — SHARED CUSTOMER DETAILS TABLE
   Columns: Name, Visits, Total Spend, Last Visit, Segment, Primary Channel,
   Email, Phone. Masked email/phone with per-column eye toggles, sticky header,
   sortable columns, search, pagination (50 + Load More), row → profile.
   ═══════════════════════════════════════════════════════════════════════════ */
type SortKey = "name" | "visits" | "totalSpend" | "lastVisit" | "segment" | "primaryChannel";
/* Default page size — matches the Customer Directory so every customer table
   in the report starts at the same window size. */
const PAGE = 25;

export function CustomerDetailsTable({ customers, onRowClick, onExport }: {
    customers: CustomerRecord[];
    onRowClick: (c: CustomerRecord) => void;
    onExport: (spec: ExportFlowSpec, title: string) => void;
}) {
    const isDesktop = useIsDesktop();
    const money = useMoney();
    const [reveal, setReveal] = useState({ email: false, phone: false }); // session-scoped
    const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "totalSpend", dir: "desc" });
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE);
    const tableTopRef = useRef<HTMLDivElement>(null);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let rows = customers;
        if (q) {
            rows = customers.filter((c) => {
                if (c.name.toLowerCase().includes(q)) return true;
                if (reveal.email && c.email && c.email.toLowerCase().includes(q)) return true;
                if (reveal.phone && c.phone && c.phone.replace(/\D/g, "").includes(q.replace(/\D/g, ""))) return true;
                return false;
            });
        }
        const dir = sort.dir === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = a[sort.key] as any, bv = b[sort.key] as any;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [customers, search, sort, reveal]);

    // A filter/sort/page-size change invalidates the current page number, so the
    // view resets to page 1 — only Next/Prev ever preserve it.
    useEffect(() => { setPage(1); }, [search, sort.key, sort.dir, pageSize, customers]);

    const total = filtered.length;
    const showAll = pageSize === ALL_ROWS;
    const size = showAll ? Math.max(1, total) : pageSize;
    const totalPages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(page, totalPages);
    const offset = (safePage - 1) * size;
    const shown = filtered.slice(offset, offset + size);
    const start = total === 0 ? 0 : offset + 1;
    const end = offset + shown.length;

    const goToPage = (n: number) => {
        setPage(n);
        tableTopRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    };

    const toggleSort = (key: SortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

    const doExport = () => {
        const spec: ExportFlowSpec = {
            title: "Customers",
            filename: `customers_${new Date().toISOString().slice(0, 10)}`,
            build: (inc) => ({
                headers: ["Customer Name", "No. of Visits", "Total Spend", "Last Visit", "Segment", "Primary Channel", "Email ID", "Phone No."],
                rows: filtered.map((c) => [
                    c.name, c.visits, money(c.totalSpend), c.lastVisit, c.segment, c.primaryChannel,
                    inc ? (c.email ?? "—") : maskEmail(c.email),
                    inc ? (c.phone ?? "—") : maskPhone(c.phone),
                ]),
            }),
        };
        onExport(spec, "Customers");
    };

    const headCell = (label: string, key?: SortKey, extra?: React.ReactNode, sticky = false) => (
        <th
            onClick={key ? () => toggleSort(key) : undefined}
            style={{
                fontSize: 10, fontWeight: 600, color: T.t3, textAlign: "left", padding: "8px 12px", textTransform: "uppercase",
                letterSpacing: 0.3, whiteSpace: "nowrap", cursor: key ? "pointer" : "default",
                position: "sticky", top: 0, background: T.surface, zIndex: sticky ? 3 : 2,
                ...(sticky ? { left: 0 } : {}),
            }}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {label}
                {key && sort.key === key && <Icon name={sort.dir === "asc" ? "arrow_upward" : "arrow_downward"} size={12} color={T.accent} />}
                {extra}
            </span>
        </th>
    );

    const eyeBtn = (col: "email" | "phone") => (
        <span
            onClick={(e) => { e.stopPropagation(); setReveal((r) => ({ ...r, [col]: !r[col] })); }}
            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", marginLeft: 4 }}
            title={reveal[col] ? "Hide" : "Reveal"}
        >
            <Icon name={reveal[col] ? "visibility" : "visibility_off"} size={14} color={T.t3} />
        </span>
    );

    return (
        <div>
            <div ref={tableTopRef} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: T.hover, borderRadius: 8, flex: 1, minWidth: 180, maxWidth: 320 }}>
                    <Icon name="search" size={15} color={T.t3} />
                    <input
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ border: "none", outline: "none", background: "transparent", fontSize: 12.5, color: T.t1, width: "100%", fontFamily: T.font }}
                    />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <PageSizeSelect value={pageSize} onChange={setPageSize} totalCount={total} />
                    <button onClick={doExport} className="eod-download-btn" style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", fontSize: 12, color: T.t2, fontWeight: 500 }}>
                        <Icon name="download" size={15} color={T.t3} /> Export
                    </button>
                </div>
            </div>

            <div style={{ overflowX: "auto", maxHeight: isDesktop ? 560 : 460, overflowY: "auto", border: `1px solid ${T.divider}`, borderRadius: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                    <thead>
                        <tr>
                            {headCell("Customer Name", "name", null, true)}
                            {headCell("No. of Visits", "visits")}
                            {headCell("Total Spend", "totalSpend")}
                            {headCell("Last Visit", "lastVisit")}
                            {headCell("Segment", "segment")}
                            {headCell("Primary Channel", "primaryChannel")}
                            {headCell("Email ID", undefined, eyeBtn("email"))}
                            {headCell("Phone No.", undefined, eyeBtn("phone"))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((c) => (
                            <tr
                                key={c.id}
                                onClick={() => onRowClick(c)}
                                style={{ borderBottom: `1px solid ${T.divider}`, cursor: "pointer" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                                <td style={{ padding: "10px 12px", position: "sticky", left: 0, background: "inherit", zIndex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <Avatar name={c.name} color={segmentColor(c.segment)} size={26} />
                                        <span style={{ fontSize: 12.5, fontWeight: 500, color: T.t1, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                                            {c.name}
                                            {isRepeatDisputer(c) && <Icon name="gpp_maybe" size={14} color={T.red} title="Repeat Disputer" />}
                                        </span>
                                    </div>
                                </td>
                                <td style={tdStyle}>{c.visits}</td>
                                <td style={{ ...tdStyle, fontWeight: 600 }}>{money(c.totalSpend)}</td>
                                <td style={{ ...tdStyle, color: T.t2 }}>{c.lastVisit}</td>
                                <td style={tdStyle}><SegmentBadge label={c.segment} color={segmentColor(c.segment)} /></td>
                                <td style={{ ...tdStyle, color: T.t2 }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        <Icon name={channelIcon(c.primaryChannel)} size={14} color={T.t3} />{c.primaryChannel}
                                    </span>
                                </td>
                                <td style={{ ...tdStyle, color: T.t2, fontVariantNumeric: "tabular-nums" }}>{reveal.email ? (c.email ?? "—") : maskEmail(c.email)}</td>
                                <td style={{ ...tdStyle, color: T.t2, fontVariantNumeric: "tabular-nums" }}>{reveal.phone ? (c.phone ?? "—") : maskPhone(c.phone)}</td>
                            </tr>
                        ))}
                        {!shown.length && (
                            <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: T.t3, fontSize: 13 }}>
                                {search ? `No customers found matching "${search}"` : "No customers in this segment yet."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: T.t3 }}>
                    Showing {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
                </span>
                {total > 0 && !showAll && <PaginationBar page={safePage} totalPages={totalPages} onChange={goToPage} />}
            </div>
        </div>
    );
}
const tdStyle: React.CSSProperties = { padding: "10px 12px", fontSize: 12.5, color: T.t1, whiteSpace: "nowrap" };

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-7 — INDIVIDUAL CUSTOMER PROFILE
   ═══════════════════════════════════════════════════════════════════════════ */
export function CustomerProfile({ customer, onBack, onOpenLoyalty, segmentsComputed = true }: {
    customer: CustomerRecord;
    onBack: () => void;
    onOpenLoyalty: (c: CustomerRecord) => void;
    segmentsComputed?: boolean;
}) {
    const isDesktop = useIsDesktop();
    const money = useMoney();
    const [reveal, setReveal] = useState({ email: false, phone: false });
    const c = customer;
    const color = segmentColor(c.segment);
    const aov = c.visits ? c.totalSpend / c.visits : 0;
    const acq = acquisitionByKey(c.acquisitionKey);
    const isAtRisk = c.churnStatus === "at_risk";

    const eye = (col: "email" | "phone", masked: string, full: string | null) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{reveal[col] ? (full ?? "—") : masked}</span>
            {full && (
                <span onClick={() => setReveal((r) => ({ ...r, [col]: !r[col] }))} style={{ cursor: "pointer", display: "inline-flex" }}>
                    <Icon name={reveal[col] ? "visibility" : "visibility_off"} size={14} color={T.t3} />
                </span>
            )}
        </span>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Back + breadcrumb */}
            <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", color: T.t2, fontSize: 13, fontWeight: 600, padding: 0, alignSelf: "flex-start" }}>
                <Icon name="arrow_back" size={18} color={T.t2} /> Back
            </button>

            {/* ─── Header (CUST-7.1) ─── */}
            <SectionCard>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Avatar name={c.name} color={color} size={56} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: T.t1 }}>{c.name}</div>
                            {isRepeatDisputer(c) && (
                                <span title={`This customer has filed ${openLostDisputes(c)} disputes in the last 12 months.`} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: T.redSoft, color: T.red, fontSize: 10.5, fontWeight: 700 }}>
                                    <Icon name="gpp_maybe" size={13} color={T.red} fill={1} /> Repeat Disputer
                                </span>
                            )}
                            <SegmentBadge label={c.segment} color={color} />
                        </div>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, fontSize: 12.5, color: T.t2 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="mail" size={14} color={T.t3} />{eye("email", maskEmail(c.email), c.email)}</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="call" size={14} color={T.t3} />{eye("phone", maskPhone(c.phone), c.phone)}</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name={channelIcon(c.primaryChannel)} size={14} color={T.t3} />{c.primaryChannel}</span>
                        </div>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: 12, color: T.t3 }}>
                            <span>Member since {c.firstVisit}</span>
                            {/* CUST-19.2 — acquisition source on profile */}
                            <span>· {c.acquisitionKey === "walkin" ? "Acquired in-person" : `Acquired via: ${acq?.source ?? "—"} · ${c.acquisitionMonth}`}</span>
                        </div>
                    </div>
                    {/* Loyalty badge (CUST-7.4) */}
                    {c.loyalty.enrolled ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, background: T.accentSoft, color: T.accent, fontSize: 12, fontWeight: 700 }}>
                            <Icon name="loyalty" size={15} color={T.accent} fill={1} /> Loyalty Member · {c.loyalty.points} pts
                        </span>
                    ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, background: T.hover, color: T.t3, fontSize: 12, fontWeight: 600 }}>
                            <Icon name="loyalty" size={15} color={T.t3} /> Not enrolled
                        </span>
                    )}
                </div>

                {/* At-risk alert strip (CUST-7.1) */}
                {isAtRisk && (
                    <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: T.orangeSoft, border: `1px solid ${T.orange}33`, display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.t1 }}>
                        <Icon name="warning" size={16} color={T.orange} fill={1} />
                        This customer is overdue for a visit. Last seen {c.lastVisit} — 1.5× their usual gap of {c.avgDaysBetween} days.
                    </div>
                )}

                {/* Lifetime summary mini-cards */}
                <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(4,1fr)" : "repeat(2,1fr)", gap: 10, marginTop: 16 }}>
                    <MiniCard label="Total Visits" value={String(c.visits)} />
                    <MiniCard label="Total Spend" value={money(c.totalSpend)} info="This customer's net lifetime spend — item spend after discounts, net of refunds, excluding tax. Equals the Net Spend in their order log below." />
                    <MiniCard label="Avg Order Value" value={money(aov)} />
                    <MiniCard label="Avg Days Between" value={c.avgDaysBetween ? `${c.avgDaysBetween} days` : "—"} />
                </div>
            </SectionCard>

            {/* ─── Loyalty block (CUST-7.4) ─── */}
            {c.loyalty.enrolled && (
                <SectionCard title="Loyalty Membership" icon="loyalty" info="Read-only from the Loyalty program (single source of truth). Points balance is program-wide, shared across all outlets.">
                    <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3,1fr)" : "repeat(3,1fr)", gap: 10 }}>
                        <MiniCard label="Current Points" value={String(c.loyalty.points)} />
                        <MiniCard label="Lifetime Earned" value={String(c.loyalty.lifetimeEarned)} />
                        <MiniCard label="Lifetime Redeemed" value={String(c.loyalty.lifetimeRedeemed)} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
                        <span style={{ fontSize: 11, color: T.t3 }}>Balance is program-wide (shared across all outlets), consistent with the Loyalty Report.</span>
                        <button onClick={() => onOpenLoyalty(c)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: `1px solid ${T.accent}44`, background: T.accentSoft, color: T.accent, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            View in Loyalty Report <Icon name="open_in_new" size={14} color={T.accent} />
                        </button>
                    </div>
                </SectionCard>
            )}

            {/* ─── Dispute history (CUST-16.1) ─── */}
            {c.disputes.length > 0 && (
                <SectionCard title="Dispute History" subtitle="Trailing 12 months" icon="gavel" info={infoCopy("disputes")}>
                    <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: T.t2 }}>Total disputes: <b style={{ color: T.t1 }}>{c.disputes.length}</b></span>
                        <span style={{ fontSize: 12, color: T.t2 }}>Disputed amount: <b style={{ color: T.t1 }}>{money(c.disputes.reduce((s, d) => s + d.amount, 0))}</b></span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                            <thead><tr>{["Date", "Order", "Amount", "Reason", "Resolution"].map((h) => <th key={h} style={thMini}>{h}</th>)}</tr></thead>
                            <tbody>
                                {c.disputes.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                        <td style={tdMini}>{d.date}</td>
                                        <td style={tdMini}>{d.orderId}</td>
                                        <td style={tdMini}>{money(d.amount)}</td>
                                        <td style={tdMini}>{d.reasonCode}</td>
                                        <td style={tdMini}>
                                            {d.resolution === "won"
                                                ? <span style={{ color: T.green, fontWeight: 600 }}>Resolved — merchant favor</span>
                                                : <span style={{ color: d.resolution === "lost" ? T.red : T.orange, fontWeight: 600, textTransform: "capitalize" }}>{d.resolution}</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </SectionCard>
            )}

            {/* ─── RFM detail panel (CUST-17.1) — gated by the segment refresh (CUST-3.4) ─── */}
            <SectionCard title="RFM Detail" subtitle={segmentsComputed ? `Scores updated: ${c.rfm.computedAt}` : undefined} icon="insights">
                {!segmentsComputed ? (
                    <div style={{ padding: "20px 8px", fontSize: 12.5, color: T.t3 }}>
                        Segments not yet computed — refresh segments on the Customers dashboard to generate this customer's RFM scores.
                    </div>
                ) : (
                    <>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <RfmRow dim="R (Recency)" score={c.rfm.r} avg={c.rfm.rAvg} label={c.rfm.rLabel} />
                            <RfmRow dim="F (Frequency)" score={c.rfm.f} avg={c.rfm.fAvg} label={c.rfm.fLabel} />
                            <RfmRow dim="M (Monetary)" score={c.rfm.m} avg={c.rfm.mAvg} label={c.rfm.mLabel} />
                        </div>
                        {c.rfm.prevSegment && c.rfm.changeDate && (
                            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: c.rfm.changeDirection === "up" ? T.green : T.red }}>
                                <Icon name={c.rfm.changeDirection === "up" ? "trending_up" : "trending_down"} size={16} color={c.rfm.changeDirection === "up" ? T.green : T.red} />
                                {c.rfm.changeDirection === "up" ? "Upgraded" : "Downgraded"} from {c.rfm.prevSegment} on {c.rfm.changeDate}.
                            </div>
                        )}
                    </>
                )}
            </SectionCard>

            {/* ─── Visit timeline (CUST-7.2) + Order log (CUST-7.3) ─── */}
            <VisitTimeline orders={c.orders} money={money} />
            <OrderLog orders={c.orders} money={money} />
        </div>
    );
}

function MiniCard({ label, value, info }: { label: string; value: string; info?: string }) {
    return (
        <div style={{ background: T.bg, border: `1px solid ${T.divider}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center" }}>{label}{info && <InfoTip text={info} />}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.t1, marginTop: 4 }}>{value}</div>
        </div>
    );
}

function RfmRow({ dim, score, avg, label }: { dim: string; score: number; avg: number; label: string }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.t2, width: 120 }}>{dim}:</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.accent }}>{score}</span>
            <span style={{ fontSize: 11.5, color: T.t3 }}>(avg: {avg})</span>
            <span style={{ display: "inline-flex", gap: 3 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n} style={{ width: 12, height: 12, borderRadius: 3, background: n <= score ? T.accent : T.divider }} />
                ))}
            </span>
            <span style={{ fontSize: 12, color: T.t2 }}>{label}</span>
        </div>
    );
}

/* CUST-7.2 — Visit timeline (recent 10 + show earlier, expandable) */
function VisitTimeline({ orders, money }: { orders: CustomerOrder[]; money: (n: number) => string }) {
    const [count, setCount] = useState(10);
    const [openId, setOpenId] = useState<string | null>(null);
    const shown = orders.slice(0, count);
    return (
        <SectionCard title="Visit Timeline" subtitle={`${orders.length} visits`} icon="timeline">
            <div style={{ display: "flex", flexDirection: "column" }}>
                {shown.map((o, i) => {
                    const cancelled = o.cancellation === "full" || o.status === "Cancelled";
                    const isOpen = openId === o.id;
                    return (
                        <div key={o.id} style={{ display: "flex", gap: 12, position: "relative" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <span style={{ width: 10, height: 10, borderRadius: "50%", background: cancelled ? T.red : T.accent, marginTop: 4, flexShrink: 0 }} />
                                {i < shown.length - 1 && <span style={{ width: 2, flex: 1, background: T.divider }} />}
                            </div>
                            <div style={{ flex: 1, paddingBottom: 14 }}>
                                <div onClick={() => setOpenId(isOpen ? null : o.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 8 }}>
                                    <span style={{ fontSize: 12.5, color: T.t1, display: "flex", alignItems: "center", gap: 6, textDecoration: cancelled ? "line-through" : "none" }}>
                                        <Icon name={channelIcon(o.channel)} size={14} color={T.t3} />
                                        {o.date} · {o.time}
                                        {cancelled && <span style={{ fontSize: 9.5, fontWeight: 700, color: T.red, background: T.redSoft, padding: "1px 6px", borderRadius: 4 }}>Cancelled</span>}
                                    </span>
                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.t1 }}>{money(o.gross)}</span>
                                </div>
                                {isOpen && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: T.t2, background: T.bg, borderRadius: 8, padding: 10 }}>
                                        {o.items.map((it, j) => (
                                            <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: it.refunded ? T.red : it.cancelled ? T.orange : T.t2 }}>
                                                <span>{it.name} ×{it.qty}{it.refunded ? " (refunded)" : it.cancelled ? " (cancelled)" : ""}</span>
                                                <span>{money(it.price * it.qty)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {count < orders.length && (
                <button onClick={() => setCount((c) => c + 10)} style={{ marginTop: 4, padding: "7px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", fontSize: 12, fontWeight: 600, color: T.accent }}>Show earlier visits</button>
            )}
        </SectionCard>
    );
}

/* CUST-7.3 — Order log table with expandable rows + footer totals */
function OrderLog({ orders, money }: { orders: CustomerOrder[]; money: (n: number) => string }) {
    const [openId, setOpenId] = useState<string | null>(null);
    const [visible, setVisible] = useState(20);
    const totals = orderLogTotals(orders);
    const shown = orders.slice(0, visible);

    const cancelCell = (o: CustomerOrder) =>
        o.cancellation === "full" ? <span style={{ color: T.red, fontWeight: 600 }}>Cancelled</span>
            : o.cancellation === "partial" ? <span style={{ color: T.orange, fontWeight: 600 }}>Partial</span>
                : "—";

    const ITEM_TOTAL_INFO = "The menu value of the order before discounts, excluding tax — not the amount charged. What the customer actually paid is shown in the Paid column.";
    const PAID_INFO = "What the customer actually paid for this order — Item Total minus discounts, excluding tax. Refunds are shown separately and are netted out in the customer's lifetime Net Spend.";

    return (
        <SectionCard title="Order Log" subtitle={`${orders.length} orders`} icon="receipt_long">
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
                    <thead>
                        <tr>
                            {["Order Date", "Order Time", "Order ID", "Channel", "Items Ordered"].map((h) => <th key={h} style={thMini}>{h}</th>)}
                            <th style={thMini}><span style={{ display: "inline-flex", alignItems: "center" }}>Item Total<InfoTip text={ITEM_TOTAL_INFO} /></span></th>
                            <th style={thMini}>Discount</th>
                            <th style={thMini}><span style={{ display: "inline-flex", alignItems: "center" }}>Paid<InfoTip text={PAID_INFO} /></span></th>
                            <th style={thMini}>Refund Amount</th>
                            <th style={thMini}>Cancellation Status</th>
                            <th style={thMini}>Order Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((o) => {
                            const isOpen = openId === o.id;
                            const is3P = o.items.length === 1 && /3P order/i.test(o.items[0].name);
                            const itemsStr = o.items.map((it) => `${it.name} ×${it.qty}`).join(", ");
                            const itemsTrunc = itemsStr.length > 40 ? `${itemsStr.slice(0, 40).trimEnd()}…` : itemsStr;
                            const disc = orderDiscountTotal(o);
                            return (
                                <Fragment key={o.id}>
                                    <tr onClick={() => setOpenId(isOpen ? null : o.id)} style={{ borderBottom: `1px solid ${T.divider}`, cursor: "pointer" }}>
                                        <td style={tdMini}>{o.date}</td>
                                        <td style={tdMini}>{o.time}</td>
                                        <td style={{ ...tdMini, display: "flex", alignItems: "center", gap: 4 }}>{o.id}{o.disputed && <span style={{ fontSize: 9, fontWeight: 700, color: T.red, background: T.redSoft, padding: "1px 5px", borderRadius: 4 }}>Disputed</span>}</td>
                                        <td style={tdMini}>{o.channel}</td>
                                        <td style={{ ...tdMini, maxWidth: 240 }} title={is3P ? undefined : itemsStr}>
                                            {is3P ? <span style={{ color: T.t3, fontStyle: "italic" }}>3P order — item details unavailable</span> : itemsTrunc}
                                        </td>
                                        <td style={{ ...tdMini, fontWeight: 600 }}>{money(o.gross)}</td>
                                        <td style={tdMini}>{disc ? <span title={o.discounts.map((d) => `${d.source}: ${money(d.amount)}`).join(", ")}>-{money(disc)}{o.discounts.length === 1 ? ` (${o.discounts[0].source})` : ""}</span> : "—"}</td>
                                        <td style={{ ...tdMini, fontWeight: 600 }}>{money(o.gross - disc)}</td>
                                        <td style={tdMini}>{o.refund ? <span style={{ color: T.red }}>-{money(o.refund)}</span> : "—"}</td>
                                        <td style={tdMini}>{cancelCell(o)}</td>
                                        <td style={tdMini}>{o.status}</td>
                                    </tr>
                                    {isOpen && (
                                        <tr>
                                            <td colSpan={11} style={{ padding: "8px 12px", background: T.bg }}>
                                                {o.items.map((it, j) => (
                                                    <div key={j} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", color: it.refunded ? T.red : it.cancelled ? T.orange : T.t2 }}>
                                                        <span>{it.name} ×{it.qty}{it.refunded ? ` — refunded${it.refundReason ? ` ("${it.refundReason}"${o.refundStaff ? `, by ${o.refundStaff}` : ""})` : ""}` : ""}{it.cancelled ? ` — cancelled${it.cancelReason ? ` (${it.cancelReason})` : ""}` : ""}</span>
                                                        <span>{money(it.price * it.qty)}</span>
                                                    </div>
                                                ))}
                                                {o.discounts.length > 1 && (
                                                    <div style={{ marginTop: 6, fontSize: 11.5, color: T.t3 }}>Discounts: {o.discounts.map((d) => `${d.source} ${money(d.amount)}`).join(" · ")}</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: T.bg, fontWeight: 700 }}>
                            <td style={{ ...tdMini, fontWeight: 700 }} colSpan={5}>Totals · {totals.count} orders</td>
                            <td style={{ ...tdMini, fontWeight: 700 }}>{money(totals.gross)}</td>
                            <td style={{ ...tdMini, fontWeight: 700 }}>-{money(totals.discounts)}</td>
                            <td style={{ ...tdMini, fontWeight: 700 }}>{money(totals.paid)}</td>
                            <td style={{ ...tdMini, fontWeight: 700 }}>-{money(totals.refunds)}</td>
                            <td style={tdMini} colSpan={2}>Net Spend {money(totals.net)} · {totals.cancelled} cancelled</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            {visible < orders.length && (
                <button onClick={() => setVisible((v) => v + 20)} style={{ marginTop: 10, padding: "7px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", fontSize: 12, fontWeight: 600, color: T.accent }}>Load More</button>
            )}
        </SectionCard>
    );
}

const thMini: React.CSSProperties = { fontSize: 10, fontWeight: 600, color: T.t3, textAlign: "left", padding: "7px 10px", textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap", borderBottom: `1px solid ${T.divider}` };
const tdMini: React.CSSProperties = { padding: "8px 10px", fontSize: 12, color: T.t1, whiteSpace: "nowrap" };

